import React from 'react';
import { Container, Typography, Paper, Box, Accordion, AccordionSummary, AccordionDetails, Divider, Button } from '@mui/material';
import { ExpandMore, Help, QuestionAnswer, LibraryBooks, Warning, Info, ContactSupport, DataUsage } from '@mui/icons-material';

const FAQPage = () => {
  const faqs = [
    {
      question: 'Всички читалища в България ли са включени в Барометъра?',
      answer: 'В Общинския барометър са включени всички читалища, които са вписани в Публичния регистър на народните читалища и на читалищните сдружения към Министерството на културата и притежават валиден БУЛСТАТ или ЕИК номер. Към момента на изготвяне на базата данни това са 3597 институции.',
      icon: <LibraryBooks />
    },
    {
      question: 'Има ли читалища, които не са включени?',
      answer: 'Да. Не са включени около 170 записа от официалния регистър, които представляват празни профили (без ЕИК, адрес или контакти), тестови записи или институции, за които е установено, че са закрити или недействащи.',
      icon: <Warning />
    },
    {
      question: 'Достоверни ли са данните за броя на персонала и субсидиите?',
      answer: 'За целите на Барометъра за най-достоверни са приети данните от Националния статистически институт (НСИ) и Министерството на финансите (МФ). Данните за персонала и дейностите, подадени директно от читалищата в техните информационни карти, са ползвани селективно, тъй като те не подлежат на външна верификация и често съдържат разминавания.',
      icon: <Info />
    },
    {
      question: 'Защо за някои общини липсва детайлна информация за персонала и финансите?',
      answer: 'Съгласно Закона за статистиката, НСИ и НАП не предоставят данни за общини, в които работят три или по-малко читалища. Това се прави с цел опазване на поверителността и предотвратяване на разкриването на индивидуални данни. Поради тази причина информацията за общо 20 общини (като Трън, Копривщица и др.) е силно ограничена.',
      icon: <Warning />
    },
    {
      question: 'Защо последната налична информация за дейността е от 2023 г.?',
      answer: 'Към януари 2026 г. Регистърът на народните читалища не функционира технически от повече от година. Това блокира възможността на читалищата да подават своите отчети за 2024 г., поради което тези данни не са налични в официалните източници. В тази връзка обхватът на цялото изследване е ограничен до периода 2021-2023 г.',
      icon: <Warning />
    },
    {
      question: 'Можем ли да разграничим данните за градските и селските читалища?',
      answer: 'Към момента официалната информация от водещите институции (МФ, НСИ, НАП) се предоставя обобщено на ниво община. Това ограничава възможността за автоматизиран анализ на спецификите между читалищата в градовете и тези в селата в рамките на настоящата версия на Барометъра.',
      icon: <DataUsage />
    },
    {
      question: 'Какво да направя, ако открия неточност в данните за моята община?',
      answer: 'Барометърът се базира на официални административни източници. Ако откриете разминаване, моля информирайте ни чрез формата за "Обратна връзка". Вашите сигнали ни помагат да идентифицираме дефекти в официалната отчетност и да подобряваме качеството на анализите.',
      icon: <ContactSupport />
    },
    {
      question: 'Мога ли да използвам данните за публикации или изследвания?',
      answer: 'Да. Данните от Общинския барометър са публични и могат да се използват за аналитични, образователни и журналистически цели. При използването им е задължително посочването на източника: "Общински барометър на читалищата – проект на фондация „Народни читалища" и съответната държавна институция, предоставила първичните данни.',
      icon: <Info />
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" gap={2} mb={4}>
          <Help color="primary" sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Общински барометър на читалищата
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Отговори на ключови въпроси относно данните и методологията
            </Typography>
          </Box>
        </Box>
        
        <Divider sx={{ mb: 4 }} />
        
        <Box>
          {faqs.map((faq, index) => (
            <React.Fragment key={index}>
              <Accordion sx={{ mb: index === faqs.length - 1 ? 0 : 1 }}>
                <AccordionSummary 
                  expandIcon={<ExpandMore />}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.02)'
                    }
                  }}
                >
                  <Box display="flex" alignItems="flex-start" gap={2}>
                    <Box sx={{ color: 'primary.main', mt: 0.5 }}>
                      {faq.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 500, fontSize: '1.05rem' }}>
                        {faq.question}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" sx={{ 
                    pl: 4, 
                    lineHeight: 1.6,
                    '& strong': {
                      color: 'primary.main'
                    }
                  }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
              {index < faqs.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </Box>
        
        <Box sx={{ mt: 6, p: 3, backgroundColor: 'rgba(25, 118, 210, 0.05)', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
            Имате допълнителен въпрос?
          </Typography>
          <Typography paragraph sx={{ mb: 2 }}>
            Ако не сте намерили отговор на вашия въпрос или имате предложение за подобрение, 
            моля свържете се с нас чрез формата за обратна връзка.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="contained" 
              startIcon={<ContactSupport />}
              href="/contact" // Променете този линк според вашите маршрути
            >
              Обратна връзка
            </Button>
            <Button 
              variant="outlined"
              href="/methodology" // Променете този линк според вашите маршрути
            >
              Вижте методологията
            </Button>
          </Box>
        </Box>
        
        <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            <strong>Важно:</strong> Всички данни в Общинския барометър са актуални към декември 2025 г. 
            Платформата се поддържа и актуализира от фондация "Народни читалища" в партньорство с институциите, 
            предоставили официалните данни.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default FAQPage;